import React, { useContext } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import { getLiveFlexSectionColumns } from "../../utils/builderUtils";

const useSectionColumns = () => {
  const [contextData, builder] = useContext(VariantContextData);
  let variant = contextData.objects.variant;

  const SectionColumns = {
    create: async function (section, callback) {
      builder.create(
        "section_columns",
        {
          website_id: section.website_id,
          section_id: section.id,
          options: {},
        },
        { callback: callback, skip_select: true }
      );
    },

    duplicate: async (og_section_column) => {
      dispatchCustomEvent("duplicateObject", {
        object_type: "section_columns",
        object_id: og_section_column.id,
      });
      return Promise.resolve();
    },

    remove: (section_column) => {
      let message =
        "Are you sure you want to remove this column? Once your changes are saved, it will be irreversibly deleted.";

      if (!confirm(message)) return;

      // Removing a column shrinks the row, so clamp columns-per-row (and its
      // mobile counterpart) down to the remaining column count — otherwise a
      // stale higher value keeps wrapping columns against a count that no
      // longer exists. Applied via skip_history updates and bundled into the
      // remove's history change so a single undo restores both.
      const updates = [];
      const section =
        variant.sections && variant.sections[section_column.section_id];

      if (section) {
        const remaining = getLiveFlexSectionColumns(section, variant).filter(
          (column) => column.id !== section_column.id
        ).length;
        const structure = (section.options && section.options.structure) || {};

        ["columns-per-row", "columns-per-row-mobile"].forEach((key) => {
          const current = parseInt(structure[key], 10);
          if (!Number.isNaN(current) && current > remaining) {
            const update = {
              object_type: "sections",
              object_id: section.id,
              setting_name: `[options][structure][${key}]`,
              value: remaining > 1 ? String(remaining) : "",
              old_value: structure[key],
            };
            updates.push(update);
            builder.update([{ ...update, skip_history: true }]);
          }
        });
      }

      builder.remove("section_columns", section_column.id, null, updates);
    },

    sortableStart: (section, container) => {
      if (!container) return null;

      let item_selector =
        ".setting[data-setting_type='item_v2'][data-object_type='section_columns']";

      let sortable_options = {
        sort: true,
        group: { name: "section-columns-position-" + section.id },
        animation: 0,
        draggable: item_selector,
        ghostClass: "drop-zone",
        handle: ".cf-item-v2[data-object_type='section_columns']",
        scroll: true,
        forceFallback: true,
        fallbackTolerance: 5,
        scrollSensitivity: 100,
        scrollSpeed: 10,
        onUpdate: (event) => {
          SectionColumns.sortableUpdate(event, section);
        },
      };
      return new Sortable(container, sortable_options);
    },

    sortableUpdate: (event, section) => {
      const updates = [];

      Array.from(event.to.children).forEach((item, index) => {
        if (
          item.getAttribute("data-setting_type") !== "item_v2" ||
          item.getAttribute("data-object_type") !== "section_columns"
        ) {
          return;
        }
        updates.push({
          object_type: "section_columns",
          object_id: parseInt(item.getAttribute("data-object_id")),
          setting_name: "[position]",
          value: index + 1,
        });
      });

      builder.update(updates);
    },

    upgradeSectionToFlex: function (section) {
      if (section.content_toggle_item_id) {
        dispatchCustomEvent("duplicateObject", {
          object_type: "elements",
          object_id: section.element_id,
          duplicate_params: { upgrade_in_place: true, section_id: section.id },
        });
      } else if (section.element_id) {
        dispatchCustomEvent("duplicateObject", {
          object_type: "elements",
          object_id: section.element_id,
          duplicate_params: { upgrade_to_flex: true },
        });
      } else {
        dispatchCustomEvent("duplicateObject", {
          object_type: "sections",
          object_id: section.id,
          duplicate_params: { upgrade_to_flex: true },
        });
      }
    },
  };

  return { SectionColumns };
};

export default useSectionColumns;
